import type {
  Ability,
  AbilityShape,
  Agent,
  Effect,
  EffectCategory,
  Reference,
  ShapeKind,
  Utility,
} from "../api/types";

/**
 * Everything needed to draw an ability: its icon, its name, and the shape it
 * takes on the map.
 *
 * The replay identifies an ability by the class of the actor it spawned
 * (GameObject_Rift_E_SmokeZone) and the key it sits on. The reference names it
 * and provides the icon. The shape — whether it is a point, a zone with a
 * radius, or a wall — comes from our own table, because the replay gives a
 * zone's position but never its radius.
 */
export interface AbilityInfo {
  icon?: string;
  name: string;
  kind: ShapeKind;
  radiusCm: number;
  detectCm: number;
  lengthCm: number;
  widthCm: number;
  coneDeg: number;
  rangeCm: number;
  segments: number;
  dormant: boolean;
  stateful: boolean;
  modes: string[];
  placement: "world" | "self" | "global" | "weapon";
  travels: boolean;
  advances: boolean;
  durationMs: number;
  effects: string[];
  approximate: boolean;
}

export class AbilityLookup {
  private readonly agents = new Map<string, Agent>();
  private readonly shapes = new Map<string, AbilityShape[]>();

  constructor(reference: Reference | undefined) {
    for (const agent of reference?.agents ?? []) this.agents.set(agent.codename, agent);
    for (const shape of reference?.shapes ?? []) {
      const k = key(shape.codename, shape.slot);
      this.shapes.set(k, [...(this.shapes.get(k) ?? []), shape]);
    }
  }

  /** The shape for one deployed actor. A slot can describe more than one form —
   *  a trap armed and a trap sprung are not the same object — and the most
   *  specific match wins. */
  shapeFor(codename: string, slot: string, cls: string): AbilityShape | undefined {
    const candidates = this.shapes.get(key(codename, slot));
    if (!candidates) return undefined;
    return (
      candidates.find((s) => s.classMatch !== "" && cls.includes(s.classMatch)) ??
      candidates.find((s) => s.classMatch === "")
    );
  }

  agent(codename: string): Agent | undefined {
    return this.agents.get(codename);
  }

  ability(codename: string, slot: string): Ability | undefined {
    return this.agents.get(codename)?.abilities.find((a) => a.slot === slot);
  }

  /** What an agent's ultimate costs in points, or 0 when we have no figure. */
  ultimateCost(codename: string): number {
    return this.shapeFor(codename, "Ultimate", "")?.ultimatePoints ?? 0;
  }

  /** Resolves one deployed utility instance to what should be drawn. */
  describe(u: Utility): AbilityInfo {
    const ability = this.ability(u.codename, u.apiSlot);
    const shape = this.shapeFor(u.codename, u.apiSlot, u.class);
    // A kind the table gives is authoritative; anchors only settle the case
    // where it has nothing to say.
    let kind = shape?.kind ?? defaultKind(u.category);
    if (!shape && u.wall) kind = "wall";
    return {
      icon: ability?.icon,
      name: ability?.displayName ?? shape?.label ?? readableClass(u.class),
      kind,
      radiusCm: shape?.radiusCm ?? 0,
      detectCm: shape?.detectCm ?? 0,
      lengthCm: shape?.lengthCm ?? 0,
      widthCm: shape?.widthCm ?? 0,
      coneDeg: shape?.coneDeg ?? 0,
      rangeCm: shape?.rangeCm ?? 0,
      segments: shape?.segments ?? 0,
      dormant: shape?.dormant ?? false,
      stateful: shape?.stateful ?? false,
      modes: shape?.modes ?? [],
      placement: shape?.placement ?? "world",
      travels: shape?.travels ?? false,
      advances: shape?.advances ?? false,
      durationMs: shape?.durationMs ?? 0,
      effects: shape?.effects ?? [],
      approximate: (shape?.accuracy ?? "approximate") !== "measured",
    };
  }

  /** The icon of the ability an effect came from, when it names an agent. */
  effectIcon(effect: Effect): string | undefined {
    if (!effect.source) return undefined;
    const agent = this.agents.get(effect.source);
    if (!agent) return undefined;
    // An ultimate is the one effect that names which ability it is.
    if (effect.category === "ultimate") {
      return agent.abilities.find((a) => a.slot === "Ultimate")?.icon ?? agent.icon;
    }
    // The effect name does not say which slot it belongs to, so fall back to
    // the agent portrait rather than guessing an ability.
    return agent.icon;
  }
}

function key(codename: string, slot: string): string {
  return `${codename}:${slot}`;
}

/** A category the table has no entry for still needs a sensible shape. */
function defaultKind(category: string): ShapeKind {
  if (category === "smoke") return "zone";
  if (category === "wall") return "wall";
  return "point";
}

/** Turns GameObject_Nox_WallTrap into "Nox Wall Trap" as a last resort. */
function readableClass(cls: string): string {
  return cls
    .replace(/^(GameObject|Gameobject|Projectile|Zone|Ability|Pawn)_/, "")
    .replace(/_C$/, "")
    .replace(/_/g, " ");
}

/** Colours and labels for the states a player can be in. */
export const EFFECT_STYLE: Record<EffectCategory, { color: string; label: string }> = {
  blind: { color: "#fff6b0", label: "aveuglé" },
  smoke: { color: "#b7c0cc", label: "dans la fumée" },
  ultimate: { color: "#ffd166", label: "ultime" },
  suppress: { color: "#ff6b3d", label: "supprimé" },
  concuss: { color: "#ffb347", label: "commotionné" },
  nearsight: { color: "#b98cff", label: "myopie" },
  reveal: { color: "#ffd166", label: "révélé" },
  heal: { color: "#5ddc7a", label: "soigné" },
  fragile: { color: "#ff8ab5", label: "fragilisé" },
  trapped: { color: "#ff7ad9", label: "immobilisé" },
  sprint: { color: "#7ad4ff", label: "sprint" },
  dash: { color: "#7ad4ff", label: "déplacement" },
  cloak: { color: "#9aa4b5", label: "invisible" },
  empowered: { color: "#ffd166", label: "renforcé" },
  downed: { color: "#9aa4b5", label: "à terre" },
  rope: { color: "#9aa4b5", label: "corde" },
  planting: { color: "#ff3b3b", label: "pose du spike" },
  defusing: { color: "#7ad4ff", label: "désamorçage" },
};

/** Effects that matter enough to mark on the map; the rest stay in the panel. */
export const MAP_EFFECTS: EffectCategory[] = [
  "ultimate",
  "blind",
  "suppress",
  "concuss",
  "nearsight",
  "trapped",
  "reveal",
  "heal",
];

/** Active effects of one player at an instant. An effect with no end runs to
 *  the end of its round. */
export function effectsAt(effects: Effect[], pawn: number, timeMs: number, roundEndMs: number): Effect[] {
  return effects.filter(
    (e) => e.pawn === pawn && e.startMs <= timeMs && (e.endMs ?? roundEndMs) >= timeMs,
  );
}

/**
 * Which of an ability's two outcomes actually happened. Miks's M-pulse either
 * heals or concusses and the deployed object looks the same either way, so the
 * answer comes from the effects it left on players while it was live.
 */
export function utilityMode(u: Utility, modes: string[], effects: Effect[]): string | null {
  if (modes.length === 0) return null;
  const from = u.spawnedMs;
  const to = u.despawnedMs ?? Number.MAX_SAFE_INTEGER;
  for (const effect of effects) {
    if (effect.source !== u.codename) continue;
    if (!modes.includes(effect.category)) continue;
    if (effect.startMs < from - 1000 || effect.startMs > to) continue;
    return effect.category;
  }
  return null;
}

/**
 * The player an ability caught, from the effect it left on them. Deadlock's
 * nanowire and Iso's arena both take hold of someone, and the replay says who
 * by applying the effect to their pawn rather than by moving the ability.
 */
export function capturedPawn(u: Utility, effects: Effect[]): number | null {
  const from = u.spawnedMs - 500;
  const to = u.spawnedMs + 8000;
  for (const effect of effects) {
    if (effect.source !== u.codename) continue;
    if (effect.category !== "trapped") continue;
    if (effect.pawn === u.owner) continue;
    if (effect.startMs < from || effect.startMs > to) continue;
    return effect.pawn;
  }
  return null;
}

/** What a deployed ability was doing at an instant. An ability that never says
 *  otherwise is doing its job and is visible. */
export function utilityStateAt(u: Utility, timeMs: number): { active: boolean; hidden: boolean } {
  let state = { active: true, hidden: false };
  for (const change of u.states ?? []) {
    if (change.t > timeMs) break;
    state = { active: change.active, hidden: change.hidden };
  }
  return state;
}

/**
 * Facing of an ability at an instant, in radians, or null when it has none. A
 * wall points along its own span; anything else along the yaw it replicated,
 * turned smoothly between samples so a panning camera sweeps rather than jumps.
 */
export function utilityHeading(u: Utility, timeMs: number): number | null {
  if (u.wall) {
    const dx = u.wall.endX - u.wall.startX;
    const dy = u.wall.endY - u.wall.startY;
    if (dx !== 0 || dy !== 0) return Math.atan2(dy, dx);
  }
  const path = u.path;
  if (path && path.length > 0) {
    let yaw = path[0]!.yaw;
    for (let i = 0; i < path.length; i++) {
      const point = path[i]!;
      if (point.t > timeMs) break;
      yaw = point.yaw;
      const next = path[i + 1];
      if (next && next.t > timeMs) {
        const span = next.t - point.t;
        const t = span > 0 ? (timeMs - point.t) / span : 0;
        yaw = point.yaw + shortestTurn(point.yaw, next.yaw) * t;
        break;
      }
    }
    if (yaw !== 0) return (yaw * Math.PI) / 180;
  }
  if (u.yaw !== 0) return (u.yaw * Math.PI) / 180;
  return null;
}

/** The signed turn from one heading to another, never going the long way. */
function shortestTurn(from: number, to: number): number {
  let delta = (to - from) % 360;
  if (delta > 180) delta -= 360;
  if (delta < -180) delta += 360;
  return delta;
}

/** How far along its travel an advancing ability has got, from 0 to 1. An
 *  ability whose whole area lands at once is finished the moment it appears. */
export function sweepProgress(u: Utility, timeMs: number, advances: boolean): number {
  if (!advances) return 1;
  const path = u.path;
  if (path && path.length > 1) {
    const span = path[path.length - 1]!.t - path[0]!.t;
    if (span > 0) return clamp01((timeMs - path[0]!.t) / span);
  }
  // No trajectory was replicated: assume the two seconds the game takes.
  return clamp01((timeMs - u.spawnedMs) / 2000);
}

function clamp01(n: number): number {
  return n < 0 ? 0 : n > 1 ? 1 : n;
}

/** Position of a moving ability at an instant, interpolated along its path. */
export function utilityPositionAt(u: Utility, timeMs: number): [number, number] | null {
  const path = u.path;
  if (!path || path.length === 0) {
    if (u.x === undefined || u.y === undefined) return null;
    return [u.x, u.y];
  }
  if (timeMs <= path[0]!.t) return [path[0]!.x, path[0]!.y];
  const last = path[path.length - 1]!;
  if (timeMs >= last.t) return [last.x, last.y];

  let lo = 0;
  let hi = path.length - 1;
  while (lo + 1 < hi) {
    const mid = (lo + hi) >> 1;
    if (path[mid]!.t <= timeMs) lo = mid;
    else hi = mid;
  }
  const a = path[lo]!;
  const b = path[lo + 1]!;
  const span = b.t - a.t;
  const t = span > 0 ? (timeMs - a.t) / span : 0;
  return [a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t];
}
